import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getApiUrl } from '../config/api';
import './CreatorOffer.css';

interface OfferData {
  creator: {
    platform: string;
    handle: string;
    followers: number;
    medianViews: number;
    averageViews: number;
  };
  pricing: {
    originalPrice?: number;
    proposedPrice: number;
    weightedCPM: number;
    expectedCPM: number;
  };
  audience: { [key: string]: number };
  status: string;
  negotiationType?: 'auto' | 'manual';
  reasoning?: {
    audienceDistribution: string;
    cpmCalculation: string;
    viewMetric: string;
  };
}

const CreatorOffer: React.FC = () => {
  const { platform, username } = useParams<{ platform: string; username: string }>();
  const [offerData, setOfferData] = useState<OfferData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [decision, setDecision] = useState<'accepted' | 'rejected' | null>(null);

  useEffect(() => {
    fetchOfferData();
  }, [platform, username]);

  const fetchOfferData = async () => {
    try {
      // Determine which endpoint to use based on URL path
      const isManualOffer = window.location.pathname.includes('/manual-offer');
      const endpoint = isManualOffer
        ? `/api/creators/${platform}/${username}/manual-offer`
        : `/api/creators/${platform}/${username}/offer`;

      const response = await fetch(getApiUrl(endpoint));
      if (!response.ok) {
        throw new Error('Failed to fetch offer data');
      }
      const data = await response.json();
      if (data.success) {
        setOfferData(data.data);
      } else {
        throw new Error(data.error || 'Failed to load offer');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setIsProcessing(true);
    try {
      const isManualOffer = window.location.pathname.includes('/manual-offer');
      const endpoint = isManualOffer
        ? `/api/creators/${platform}/${username}/manual-offer/accept`
        : `/api/creators/${platform}/${username}/offer/accept`;

      const response = await fetch(getApiUrl(endpoint), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to accept offer');
      }

      const data = await response.json();
      if (data.success) {
        setDecision('accepted');
      } else {
        throw new Error(data.error || 'Failed to accept offer');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to accept offer');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    setIsProcessing(true);
    try {
      const isManualOffer = window.location.pathname.includes('/manual-offer');
      const endpoint = isManualOffer
        ? `/api/creators/${platform}/${username}/manual-offer/reject`
        : `/api/creators/${platform}/${username}/offer/reject`;

      const response = await fetch(getApiUrl(endpoint), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to reject offer');
      }

      const data = await response.json();
      if (data.success) {
        setDecision('rejected');
      } else {
        throw new Error(data.error || 'Failed to reject offer');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to reject offer');
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="offer-container">
        <div className="offer-loading">Loading offer details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="offer-container">
        <div className="offer-error">
          <h2>Offer Not Found</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!offerData) {
    return (
      <div className="offer-container">
        <div className="offer-error">No offer data available</div>
      </div>
    );
  }

  if (decision) {
    return (
      <div className="offer-container">
        <div className="offer-card">
          <div className={`decision-message ${decision}`}>
            <h2>{decision === 'accepted' ? '✅ Offer Accepted!' : '❌ Offer Declined'}</h2>
            <p>
              {decision === 'accepted' 
                ? 'Thank you for accepting our offer. We look forward to working with you!'
                : 'Thank you for considering our offer. We hope to work together in the future.'}
            </p>
          </div>
        </div>
      </div>
    );
  }


  const isManualNegotiation = offerData.negotiationType === 'manual';

  return (
    <div className="offer-container">
      <div className="offer-card">
        <div className="offer-header">
          <h1>Collaboration Proposal</h1>
          <p className="creator-info">@{offerData.creator.handle} • {offerData.creator.platform}</p>
        </div>

        <div className="offer-section pricing-section">
          <h2>💰 Pricing Proposal</h2>
          {isManualNegotiation ? (
            <div className="pricing-simple">
              <div className="price-item proposed" style={{ maxWidth: '100%' }}>
                <span className="price-label">Our Proposed Price</span>
                <span className="price-value">${offerData.pricing.proposedPrice}</span>
              </div>
            </div>
          ) : (
            <div className="pricing-comparison">
              <div className="price-item original">
                <span className="price-label">Your Requested Price</span>
                <span className="price-value">${offerData.pricing.originalPrice}</span>
              </div>
              <div className="price-arrow">→</div>
              <div className="price-item proposed">
                <span className="price-label">Our Proposed Price</span>
                <span className="price-value">${offerData.pricing.proposedPrice}</span>
              </div>
            </div>
          )}
        </div>

        {!isManualNegotiation && offerData.reasoning && (
          <div className="offer-section calculation-section">
            <h2>📊 How We Calculate Fair Market Value</h2>
            <div className="calculation-details">
            <div className="calculation-item">
              <div className="calc-icon">👁️</div>
              <div className="calc-content">
                <strong>Your Performance Metrics</strong>
                <p>{offerData.reasoning.viewMetric}</p>
                <div className="metric-value">{offerData.creator.medianViews.toLocaleString()} views</div>
              </div>
            </div>
            
            <div className="calculation-item">
              <div className="calc-icon">🌍</div>
              <div className="calc-content">
                <strong>Audience Distribution</strong>
                <p>{offerData.reasoning.audienceDistribution}</p>
                <div className="audience-breakdown">
                  {Object.entries(offerData.audience)
                    .sort(([, a], [, b]) => b - a)
                    .slice(0, 5)
                    .map(([country, percentage]) => (
                      <div key={country} className="audience-item">
                        <span className="country">{country.toUpperCase()}</span>
                        <div className="percentage-bar">
                          <div 
                            className="percentage-fill" 
                            style={{ width: `${percentage * 100}%` }}
                          />
                        </div>
                        <span className="percentage">{(percentage * 100).toFixed(0)}%</span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
            
            <div className="calculation-item">
              <div className="calc-icon">💵</div>
              <div className="calc-content">
                <strong>Market CPM Rate</strong>
                <p>{offerData.reasoning.cpmCalculation}</p>
                <div className="metric-value">${offerData.pricing.weightedCPM.toFixed(2)} CPM</div>
              </div>
            </div>
          </div>

          <div className="formula-explanation">
            <h3>📐 Formula</h3>
            <code>
              Fair Price = Median Views × Market CPM / 1000
            </code>
            <code className="formula-calculation">
              ${offerData.pricing.proposedPrice} = {offerData.creator.medianViews.toLocaleString()} × ${offerData.pricing.weightedCPM.toFixed(2)} / 1000
            </code>
          </div>
          </div>
        )}

        <div className="offer-section action-section">
          <h2>🤝 Ready to Collaborate?</h2>
          <p>
            {isManualNegotiation
              ? 'We believe this is a fair offer that reflects the value of your content. We look forward to working with you!'
              : 'We believe this pricing reflects the true value of your content and ensures a fair partnership for both parties. Your content quality and audience engagement are valuable to us, and we\'re excited about the possibility of working together.'}
          </p>
          
          <div className="action-buttons">
            <button 
              className="btn-accept"
              onClick={handleAccept}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Accept Offer'}
            </button>
            <button 
              className="btn-reject"
              onClick={handleReject}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Decline Offer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatorOffer;